import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ImagePlus, X, ZoomIn, RotateCw, Maximize2, Info } from 'lucide-react';
import { uploadAPI } from '@/lib/api';
import { resolveMediaUrl } from '@/lib/utils';

// Preset aspect ratios for different contexts
const PRESETS = {
  cover: { aspect: 16 / 9, label: 'Copertina', hint: '1200 × 675 px (16:9)', width: 1200, height: 675 },
  square: { aspect: 1, label: 'Quadrata', hint: '800 × 800 px (1:1)', width: 800, height: 800 },
  banner: { aspect: 3 / 1, label: 'Banner', hint: '1200 × 400 px (3:1)', width: 1200, height: 400 },
};

/**
 * Create a cropped image from source and crop area
 */
function getCroppedCanvas(imageSrc, pixelCrop, rotation = 0, maxWidth = Infinity) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const radian = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(radian));
      const cos = Math.abs(Math.cos(radian));
      const bBoxW = image.width * cos + image.height * sin;
      const bBoxH = image.width * sin + image.height * cos;

      canvas.width = bBoxW;
      canvas.height = bBoxH;

      ctx.translate(bBoxW / 2, bBoxH / 2);
      ctx.rotate(radian);
      ctx.translate(-image.width / 2, -image.height / 2);
      ctx.drawImage(image, 0, 0);

      // Now crop
      const cropCanvas = document.createElement('canvas');
      const cropCtx = cropCanvas.getContext('2d');
      // No bigger than the preset asks for: phone photos would otherwise upload at full size.
      const scale = Math.min(1, maxWidth / pixelCrop.width);
      cropCanvas.width = Math.round(pixelCrop.width * scale);
      cropCanvas.height = Math.round(pixelCrop.height * scale);
      cropCtx.drawImage(
        canvas,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, cropCanvas.width, cropCanvas.height
      );

      cropCanvas.toBlob(
        (blob) => resolve(blob),
        'image/jpeg',
        0.92
      );
    };
    image.src = imageSrc;
  });
}

export const ImageCropUpload = ({
  value,           // current image URL
  onChange,         // (url) => void
  preset = 'cover', // 'cover' | 'square' | 'banner'
  round = false,    // profile photos: round crop and a small round preview
  disabled = false,
  className = '',
}) => {
  const inputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgDimensions, setImgDimensions] = useState(null);

  const config = PRESETS[preset] || PRESETS.cover;

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine (JPG, PNG, WebP)');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        setImgDimensions({ width: img.width, height: img.height });
        setImageSrc(reader.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setShowCropper(true);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirmCrop = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    setUploading(true);
    try {
      const blob = await getCroppedCanvas(imageSrc, croppedAreaPixels, rotation, config.width);
      const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' });
      const res = await uploadAPI.uploadFile(file);
      onChange(res.data.url);
      toast.success('Immagine caricata!');
      setShowCropper(false);
      setImageSrc(null);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    setImgDimensions(null);
  };

  const handleRemove = () => {
    onChange('');
  };

  const isSmall = imgDimensions && (imgDimensions.width < config.width || imgDimensions.height < config.height);

  return (
    <div className={className}>
      {/* Upload button + remove */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button type="button" variant="outline" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}>
          <ImagePlus className="h-4 w-4 mr-2" />
          {value ? 'Cambia immagine' : 'Carica immagine'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={onFileSelect}
        />
        {value && (
          <Button type="button" variant="ghost" onClick={handleRemove}>
            <X className="h-4 w-4 mr-2" />
            Rimuovi
          </Button>
        )}
      </div>

      {/* Dimension hint */}
      <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
        <Info className="h-3 w-3" />
        Dimensioni consigliate: {config.hint}
      </p>

      {/* Current preview */}
      {value && (
        <div className={`mt-3 overflow-hidden border ${round ? 'w-28 rounded-full' : 'rounded-lg'}`} style={{ aspectRatio: config.aspect }}>
          <img
            src={resolveMediaUrl(value)}
            alt="Anteprima"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Crop Dialog */}
      <Dialog open={showCropper} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Maximize2 className="h-5 w-5" />
              Ritaglia immagine
            </DialogTitle>
            <DialogDescription>
              Trascina e ridimensiona l'area per ottenere il ritaglio perfetto.
            </DialogDescription>
          </DialogHeader>

          {/* Dimension warning */}
          {isSmall && (
            <div className="flex items-center gap-2 p-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-sm">
              <Info className="h-4 w-4 text-yellow-500 shrink-0" />
              <span>L'immagine ({imgDimensions.width}×{imgDimensions.height}) è più piccola di {config.width}×{config.height} — potrebbe risultare sgranata.</span>
            </div>
          )}

          {/* Cropper area */}
          <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ height: '320px' }}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={config.aspect}
                cropShape={round ? 'round' : 'rect'}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid
                style={{
                  containerStyle: { borderRadius: '8px' },
                }}
              />
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5 text-sm">
                  <ZoomIn className="h-3.5 w-3.5" />
                  Zoom
                </Label>
                <Badge variant="outline" className="text-xs">{zoom.toFixed(1)}×</Badge>
              </div>
              <Slider
                value={[zoom]}
                onValueChange={([v]) => setZoom(v)}
                min={1}
                max={3}
                step={0.05}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5 text-sm">
                  <RotateCw className="h-3.5 w-3.5" />
                  Rotazione
                </Label>
                <Badge variant="outline" className="text-xs">{rotation}°</Badge>
              </div>
              <Slider
                value={[rotation]}
                onValueChange={([v]) => setRotation(v)}
                min={0}
                max={360}
                step={1}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancel} disabled={uploading}>
              Annulla
            </Button>
            <Button onClick={handleConfirmCrop} disabled={uploading}>
              {uploading ? 'Caricamento...' : 'Conferma e carica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageCropUpload;
